/**
 * Блок «Административное деление» районной вкладки (US-11 FR-2).
 *
 * Три счётчика состава района: сельские округа · сёла · города районного
 * значения. Значения выводятся как есть, без пересчёта на стороне экрана
 * (FR-2.2) — источник объявит их отдельной операцией (US-12 FR-5).
 *
 * Заголовок блока — обычный текст: страницы административно-территориального
 * устройства района в системе нет, перехода из заголовка не выполняется
 * (FR-2.3, решение 4 от 03.09).
 *
 * Плитки выводятся всегда в полном составе и в постоянном порядке:
 * состав плашки читается как структура территории, и исчезнувшая плитка
 * читалась бы как «такого объекта здесь нет». Нет значения по счётчику
 * → плитка остаётся со своим названием, а вместо числа — «нет данных»
 * (образец — `StatCard`); «—» и «0» подставлять нельзя, они читались бы
 * как данные. Пустой ответ по всему блоку — тоже три плитки с «нет
 * данных», в одно общее «нет данных» блок не сворачивается.
 *
 * Для города областного значения блок не выводится вовсе (FR-2.5) — это
 * решает страница, здесь такого состояния нет.
 *
 * **Состав по уровню (US-41).** Если выдача объявила состав (`layout`),
 * блок рисует ровно его и в его порядке: область — пять счётчиков, район —
 * три, округ / городская администрация / село — один-два, каждый считает
 * себя. Счётчика вне состава у уровня нет — ни нулём, ни «нет данных». Счётчик
 * состава без значения — справочника по территории нет — скрывается, а
 * остальные делят ширину (FR-6). Плашка из одного-двух счётчиков их не
 * растягивает: плитки того же размера, что в многосоставной, по левому краю
 * (§6). Без `layout` (выдача прежнего вида) — фиксированный состав, как
 * описано выше.
 */
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';

export interface DistrictDivisionCounts {
  /** Сельские округа, ед. */
  okrugs: number | null;
  /** Сёла, ед. */
  villages: number | null;
  /** Города районного значения, ед. */
  cities: number | null;
  /** Районы области, ед. — только на уровне области. */
  districts?: number | null;
  /** Города областного значения, ед. — только на уровне области. */
  oblastCities?: number | null;
}

export type DivisionCounter = keyof DistrictDivisionCounts;

interface Props {
  /** null — источник не ответил / операции ещё нет → «нет данных». */
  counts: DistrictDivisionCounts | null;
  /** Состав блока из выдачи (US-41), в порядке вывода; пусто — прежний. */
  layout?: readonly DivisionCounter[];
  /** Отчётная дата блока — своя, из данных блока (FR-7.2). */
  periodLabel?: string | null;
  /** Страница области — карточки уровня области вместо районных. */
  oblast?: boolean;
}

const COUNTER_KEYS = ['okrugs', 'villages', 'cities'] as const;
/** Область: районы и города областного значения, затем округа и сёла. */
const OBLAST_COUNTER_KEYS = [
  'districts',
  'oblastCities',
  'okrugs',
  'villages',
] as const;

/** Сетка под число плиток: классы литералами, иначе Tailwind их не соберёт. */
const GRID_BY_COUNT: Record<number, string> = {
  3: 'grid gap-3.5 sm:grid-cols-3',
  4: 'grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4',
  5: 'grid gap-3.5 sm:grid-cols-3 lg:grid-cols-5',
};
/** Один-два счётчика нижних уровней — размер плитки как в плашке района. */
const GRID_NARROW = 'grid gap-3.5 sm:grid-cols-3';

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);

export const DistrictDivision = ({
  counts,
  layout = [],
  periodLabel,
  oblast = false,
}: Props) => {
  const { t } = useTranslation();
  const declared = layout.length > 0;
  const keys: readonly DivisionCounter[] = declared
    ? layout.filter((key) => (counts?.[key] ?? null) !== null)
    : oblast
      ? OBLAST_COUNTER_KEYS
      : COUNTER_KEYS;
  const gridClass = declared
    ? layout.length <= 2
      ? GRID_NARROW
      : (GRID_BY_COUNT[keys.length] ?? GRID_NARROW)
    : oblast
      ? GRID_BY_COUNT[4]
      : GRID_BY_COUNT[3];

  return (
    <Card className="shadow-card">
      <CardHeader className="flex-row items-baseline justify-between space-y-0">
        {/* Обычный текст, не ссылка (FR-2.3). */}
        <CardTitle>{t('digitalPassport.districtDivision.title')}</CardTitle>
        {periodLabel != null && (
          <span
            className="text-sm text-text-muted"
            data-testid="district-division-period"
          >
            {periodLabel}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <section data-testid="block-district-division">
          <ul className={gridClass} data-testid="district-division-counters">
            {keys.map((key) => {
              const value = counts?.[key] ?? null;
              return (
                <li
                  key={key}
                  data-testid={`district-division-${key}`}
                  className="flex h-full flex-col rounded-2xl border border-surface-border bg-gradient-to-b from-surface to-surface-page p-5 shadow-card"
                >
                  {value === null ? (
                    <span
                      className="block text-sm lowercase text-text-muted"
                      data-testid={`district-division-${key}-nodata`}
                    >
                      {t('common.noData')}
                    </span>
                  ) : (
                    <span className="block text-2xl font-extrabold tabular-nums text-brand-600">
                      {fmt(value)}
                    </span>
                  )}
                  <span className="mt-1 block text-sm leading-snug text-text-muted">
                    {t(`digitalPassport.districtDivision.${key}`)}
                  </span>
                  {value !== null && (
                    <span className="mt-0.5 block text-xs text-text-faint">
                      {t('digitalPassport.districtDivision.unit')}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      </CardContent>
    </Card>
  );
};
